import React from 'react';
import styled from 'styled-components';
import { formatMoney } from '../../utils/functions';
import { PaymentRequest } from '../../hooks/useCuentasPorCobrar';

const OrderInfo = styled.div`
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
  
  & > div {
    margin-bottom: 0.25rem;
  }
  
  b {
    color: #495057;
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 0.5rem;
  
  & > div {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  
  label {
    font-weight: 500;
    color: #495057;
    font-size: 0.9rem;
  }
  
  input, select {
    padding: 0.375rem 0.75rem;
    border: 1px solid #ced4da;
    border-radius: 0.375rem;
    font-size: 0.95rem;
    
    &:focus {
      outline: none;
      border-color: #86b7fe;
      box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    }
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const PaymentButton = styled.button`
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: ${props => props.disabled ? '#6c757d' : '#0d6efd'};
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover {
    background-color: ${props => props.disabled ? '#6c757d' : '#0b5ed7'};
  }
`;

interface PaymentRequestModalProps {
  selectedOrder: any;
  paymentRequest: PaymentRequest;
  exceedsBalance: boolean;
  onClose: () => void;
  onSubmit: (amount: number, date: string, method: string, notes?: string) => Promise<void>;
  onUpdateRequest: (field: keyof PaymentRequest, value: string | number) => void;
}

export const PaymentRequestModal: React.FC<PaymentRequestModalProps> = ({
  selectedOrder,
  paymentRequest,
  exceedsBalance,
  onClose,
  onSubmit,
  onUpdateRequest
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async () => {
    if (!selectedOrder) return;
    
    setIsSubmitting(true);
    try {
      await onSubmit(
        Number(paymentRequest.amount), // Convertir a número solo al enviar
        paymentRequest.date,
        paymentRequest.method || '',
        paymentRequest.notes
      );
      onClose();
    } catch (error: any) {
      console.error('Error submitting payment request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!selectedOrder) return null;

  return (
    <>
      <OrderInfo>
        <div><b>Cliente:</b> {selectedOrder.customerName}</div>
        <div><b>Saldo pendiente:</b> {formatMoney(
          Number(selectedOrder.toPayAdjusted ?? selectedOrder.toPay)
        )}</div>
      </OrderInfo>

      <FormGrid>
        <div>
          <label>Fecha</label>
          <input
            type="date"
            value={paymentRequest.date}
            onChange={(e) => onUpdateRequest('date', e.target.value)}
          />
        </div>
        
        <div>
          <label>Monto</label>
          <input
            type="text"
            step="0.01"
            value={paymentRequest.amount || ''}
            onChange={(e) => {
              let value = e.target.value;
              
              // Permitir solo números y un punto decimal
              if (value.split('.').length > 2) {
                // Si hay más de un punto, mantener solo los primeros dos segmentos
                const parts = value.split('.');
                value = parts[0] + '.' + parts.slice(1).join('');
              }
              
              // Remover caracteres no numéricos excepto un punto
              value = value.replace(/[^0-9.]/g, '');
              
              const numValue = value === '' ? 0 : parseFloat(value);
              if (!isNaN(numValue) && numValue >= 0) {
                onUpdateRequest('amount', value); // Mantener como string para preservar el input
              }
            }}
            placeholder="0.00"
            disabled={isSubmitting}
          />
          {exceedsBalance && (
            <ErrorMessage>
              El monto excede el saldo pendiente considerando solicitudes previas.
            </ErrorMessage>
          )}
        </div>
        
        <div>
          <label>Método</label>
          <select
            value={paymentRequest.method || ''}
            onChange={(e) => onUpdateRequest('method', e.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Selecciona método</option>
            <option value="EFECTIVO">Efectivo</option>
            <option value="TRANSFERENCIA">Transferencia</option>
          </select>
        </div>
        
        <div>
          <label>Notas</label>
          <input
            type="text"
            placeholder="Referencia u observaciones"
            value={paymentRequest.notes || ''}
            onChange={(e) => onUpdateRequest('notes', e.target.value)}
            disabled={isSubmitting}
          />
        </div>
      </FormGrid>

      <PaymentButton
        onClick={handleSubmit}
        disabled={exceedsBalance || !paymentRequest.amount || !paymentRequest.method || isSubmitting}
      >
        {isSubmitting ? 'Enviando...' : 'Enviar solicitud'}
      </PaymentButton>
    </>
  );
};